import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { fileDisplayLabel, type SkippedFile } from '@/lib/folderTriage';
import { brandColors } from '@/theme/brandColors';

export type FolderTriagePending = {
  selected: File[];
  skipped: SkippedFile[];
};

type Props = {
  pending: FolderTriagePending | null;
  onConfirm: (files: File[]) => void;
  onCancel: () => void;
};

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * App-eigener Bestätigungsdialog nach einem Ordner-Upload: zeigt, welche
 * Unterlagen die Triage übernehmen will und was (mit Begründung) aussortiert
 * wurde. Erst mit „Übernehmen" landen die Dateien in der Auswahl.
 */
export function FolderTriageDialog({ pending, onConfirm, onCancel }: Props) {
  const selected = pending?.selected ?? [];
  const skipped = pending?.skipped ?? [];

  // Aussortiertes nach Grund gruppieren — 17 einzelne Foto-Zeilen liest niemand.
  const skippedByReason = new Map<string, string[]>();
  for (const s of skipped) {
    const list = skippedByReason.get(s.reason) ?? [];
    list.push(s.name);
    skippedByReason.set(s.reason, list);
  }

  return (
    <Dialog
      open={!!pending}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } }}
    >
      <DialogTitle sx={{ pt: 2.5, pb: 1 }}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            bgcolor: `rgba(${brandColors.primaryRgb}, 0.12)`,
            color: 'primary.main',
            fontSize: '0.75rem',
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          <FolderOpenOutlinedIcon sx={{ fontSize: 16 }} />
          Ordner geprüft
        </Box>
        <Typography component="span" variant="h2" sx={{ display: 'block', fontWeight: 700, fontSize: '1.25rem' }}>
          {selected.length + skipped.length} Dateien gefunden
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, fontSize: '0.8125rem' }}>
          {selected.length} werden für die Analyse übernommen
          {skipped.length > 0 ? `, ${skipped.length} automatisch aussortiert.` : '.'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 1 }}>
        {selected.length > 0 && (
          <Box sx={{ mb: skipped.length ? 2 : 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em' }}>
              WIRD ÜBERNOMMEN
            </Typography>
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 200, overflowY: 'auto' }}>
              {selected.map((f, i) => (
                <Chip
                  key={`${f.name}-${f.size}-${i}`}
                  icon={<InsertDriveFileOutlinedIcon />}
                  label={`${fileDisplayLabel(f)} · ${formatSize(f.size)}`}
                  size="small"
                  sx={{ maxWidth: 320, fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {skipped.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em' }}>
              AUSSORTIERT
            </Typography>
            <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 180, overflowY: 'auto' }}>
              {[...skippedByReason.entries()].map(([reason, names]) => (
                <Box key={reason} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(15,23,42,0.035)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                    {names.length} {names.length === 1 ? 'Datei' : 'Dateien'} — {reason}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25, wordBreak: 'break-word' }}
                  >
                    {names.slice(0, 6).join(', ')}
                    {names.length > 6 ? ` … +${names.length - 6} weitere` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5 }}>
        <Button
          onClick={onCancel}
          fullWidth
          variant="outlined"
          sx={{ flex: '1 1 120px', borderColor: '#e5e7eb', color: 'text.primary', borderRadius: 1.5 }}
        >
          Abbrechen
        </Button>
        <Button
          onClick={() => onConfirm(selected)}
          disabled={!selected.length}
          fullWidth
          variant="contained"
          startIcon={<CheckCircleOutlineIcon />}
          sx={{ flex: '1 1 200px', borderRadius: 1.5, fontWeight: 600 }}
        >
          {selected.length} {selected.length === 1 ? 'Datei' : 'Dateien'} übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
